import { FormEvent, useEffect, useState } from "react";
import { Check, MessageSquare, ShieldCheck } from "lucide-react";
import { apiUrl, responseError } from "../app/api";
import { DictaLogo } from "./DictaLogo";

interface ReviewLine {
  id: string;
  line: number;
  kind: string;
  startMs: number;
  endMs: number;
  speaker: string;
  text: string;
}

interface ReviewComment {
  id: string;
  nodeId: string;
  authorName: string;
  text: string;
  createdAt: string;
}

interface ReviewPayload {
  share: {
    matterName: string;
    senderEmail: string;
    recipientName: string;
    expiresAt: string;
    status: string;
    sourcePlaybackPermitted: boolean;
  };
  transcript: ReviewLine[];
  comments: ReviewComment[];
}

type ReviewState = "checking" | "code" | "ready" | "unavailable";

export function SharedReviewTranscript({
  token,
  onNotReviewedShare
}: {
  token: string;
  onNotReviewedShare: () => void;
}) {
  const [state, setState] = useState<ReviewState>("checking");
  const [payload, setPayload] = useState<ReviewPayload | null>(null);
  const [recipientHint, setRecipientHint] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [commentNodeId, setCommentNodeId] = useState("");
  const [commentText, setCommentText] = useState("");

  const transcriptPath = `/api/shared/${encodeURIComponent(token)}/transcript`;

  const loadTranscript = async () => {
    const response = await fetch(apiUrl(transcriptPath), { credentials: "same-origin" });
    if (response.status === 404) {
      onNotReviewedShare();
      return;
    }
    if (response.status === 401) {
      setState("code");
      const challenge = await fetch(
        apiUrl(`/api/shared/${encodeURIComponent(token)}/challenge`),
        { method: "POST", credentials: "same-origin" }
      );
      if (challenge.ok) {
        const result = (await challenge.json()) as { recipientHint?: string };
        setRecipientHint(result.recipientHint ?? "the bound email address");
        return;
      }
      if (challenge.status === 429) {
        setRecipientHint("the bound email address");
        return;
      }
      throw await responseError(challenge, "The access code could not be sent.");
    }
    if (response.status === 410) {
      setState("unavailable");
      return;
    }
    if (!response.ok) throw await responseError(response, "The reviewed transcript could not load.");
    setPayload((await response.json()) as ReviewPayload);
    setState("ready");
  };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Reviewed transcript · Dicta";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow, noarchive";
    document.head.append(robots);
    void loadTranscript().catch((cause) => {
      setError(cause instanceof Error ? cause.message : "The reviewed transcript could not load.");
      setState("unavailable");
    });
    return () => {
      document.title = previousTitle;
      robots.remove();
    };
  }, [token]);

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        apiUrl(`/api/shared/${encodeURIComponent(token)}/verify`),
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code })
        }
      );
      if (!response.ok) throw await responseError(response, "The code could not be verified.");
      await loadTranscript();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The code could not be verified.");
    } finally {
      setBusy(false);
    }
  };

  const addComment = async (event: FormEvent) => {
    event.preventDefault();
    if (!commentNodeId || !commentText.trim()) return;
    setBusy(true);
    setError("");
    try {
      const response = await fetch(
        apiUrl(`/api/shared/${encodeURIComponent(token)}/comments`),
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ nodeId: commentNodeId, text: commentText })
        }
      );
      if (!response.ok) throw await responseError(response, "The comment could not be added.");
      const result = (await response.json()) as { comment: ReviewComment };
      setPayload((current) => current ? {
        ...current,
        comments: [...current.comments, result.comment]
      } : current);
      setCommentText("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The comment could not be added.");
    } finally {
      setBusy(false);
    }
  };

  if (state === "checking") {
    return <main className="shared-review-page"><p role="status">Checking secure review link…</p></main>;
  }

  if (state === "code") {
    return (
      <main className="shared-review-page shared-review-page--gate">
        <DictaLogo />
        <section className="shared-review-gate" aria-labelledby="review-code-title">
          <ShieldCheck size={24} aria-hidden="true" />
          <h1 id="review-code-title">Check your email</h1>
          <p>We sent a six-digit code to {recipientHint || "the bound email address"}. No account is required.</p>
          <form className="form-stack" onSubmit={verify}>
            <label className="field">
              <span>Six-digit code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              />
            </label>
            <button className="button button--primary" type="submit" disabled={busy || code.length !== 6}>
              {busy ? "Checking…" : "Open transcript"}
            </button>
          </form>
          {error && <p className="inline-error" role="alert">{error}</p>}
        </section>
      </main>
    );
  }

  if (state === "unavailable" || !payload) {
    return (
      <main className="shared-review-page shared-review-page--gate">
        <DictaLogo />
        <section className="shared-review-gate">
          <h1>This reviewed transcript is unavailable</h1>
          <p>{error || "The link has expired or its owner revoked access."}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="shared-review-page">
      <header className="shared-review-head">
        <DictaLogo />
        <div>
          <span className="eyebrow">Secure recipient review</span>
          <h1>{payload.share.matterName}</h1>
          <p>Shared by {payload.share.senderEmail} for {payload.share.recipientName}.</p>
        </div>
        <span className="reviewed-status"><Check size={14} aria-hidden="true" />{payload.share.status}</span>
      </header>

      <section className="shared-review-sheet" aria-label="Reviewed transcript">
        {payload.transcript.map((line) => (
          <article className="shared-review-line" key={line.id}>
            <span className="line-number">{line.line}</span>
            <div>
              {line.speaker && <strong>{line.speaker}</strong>}
              <p>{line.text}</p>
              <button className="button button--text" type="button" onClick={() => setCommentNodeId(line.id)}>
                <MessageSquare size={14} aria-hidden="true" /> Comment on line {line.line}
              </button>
              {payload.comments.filter((comment) => comment.nodeId === line.id).map((comment) => (
                <blockquote key={comment.id} className="shared-review-comment">
                  <p>{comment.text}</p>
                  <cite>{comment.authorName}</cite>
                </blockquote>
              ))}
            </div>
          </article>
        ))}
      </section>

      {commentNodeId && (
        <form className="shared-review-comment-form" onSubmit={addComment}>
          <label className="field">
            <span>Comment</span>
            <textarea maxLength={2000} value={commentText} onChange={(event) => setCommentText(event.target.value)} />
          </label>
          <div>
            <button className="button button--text" type="button" onClick={() => setCommentNodeId("")}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={busy || !commentText.trim()}>Add comment</button>
          </div>
        </form>
      )}
      {error && <p className="inline-error" role="alert">{error}</p>}

      <aside className="shared-review-next" aria-label="Try Dicta">
        <div><strong>Need to capture your own conversation?</strong><span>Try the sample without an account, or create an account for persistent work.</span></div>
        <a className="button button--primary" href="/try" aria-label="Try Dicta with a sample recording">Try it</a>
        <a className="button button--quiet" href="/api/auth/start?screen_hint=sign-up&return_to=%2Ftranscripts">Create account</a>
      </aside>
    </main>
  );
}
